import {
  breadcrumbsIntegration,
  browserProfilingIntegration,
  captureConsoleIntegration,
  consoleLoggingIntegration,
  extraErrorDataIntegration,
  httpClientIntegration,
  init,
  replayIntegration,
} from "@sentry/react"
import { getEnv } from "@holigral/shared/utils"

type Options = {
  /** The app's own API hosts: traces propagate there and nowhere else */
  tracePropagationTargets: RegExp[]
}

export default ({ tracePropagationTargets }: Options) => {
  init({
    dsn: getEnv("SENTRY_DSN"),
    environment: getEnv("ENVIRONMENT"),
    release: getEnv("RELEASE"),
    attachStacktrace: true,
    normalizeDepth: 10,
    integrations: [
      consoleLoggingIntegration({
        levels: ["info", "warn", "error", "log", "assert"],
      }),
      captureConsoleIntegration({ levels: ["error", "assert"] }),
      extraErrorDataIntegration(),
      httpClientIntegration(),
      // Click breadcrumbs otherwise serialise as the element's Tailwind class
      // soup, which says nothing about what was clicked. data-sentry="…" on a
      // control makes its breadcrumb read as the control's name instead.
      breadcrumbsIntegration({
        dom: { serializeAttribute: ["data-sentry", "data-slot"] },
      }),
      browserProfilingIntegration(),
      // Session replay, in buffer mode: nothing is uploaded until an error is
      // captured, at which point the preceding ~60s is attached to it. That
      // keeps the replay quota spent on sessions that actually broke.
      // Masking matches the SDK defaults, set explicitly so it cannot drift:
      // respondents' answers must never leave the browser.
      replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    ignoreErrors: [],
    tracesSampleRate: 1.0,
    tracePropagationTargets,
    profileSessionSampleRate: 0.1,
    profileLifecycle: "trace",
    // Buffer mode: never sample plain sessions, always keep the buffer of a
    // session that hit an error.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  })
}
